import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { useBillingStatus } from '@/composables/billing/useBillingStatus';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { buildAnalyticsUserProperties } from '@/services/analytics/userProperties';
import { useAuthStore } from '@/stores/useAuthStore';
import { useWhoAmIStore } from '@/stores/useWhoAmIStore';
import type { AnalyticsUserProperties } from '@/services/analytics';
import { watch } from 'vue';

function getPropertiesCacheKey(properties: AnalyticsUserProperties): string {
  return JSON.stringify(
    Object.fromEntries(Object.entries(properties).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))),
  );
}

export function useAnalyticsUserPropertiesSync(): void {
  const authStore = useAuthStore();
  const whoAmIStore = useWhoAmIStore();
  const { currentTenant } = useCurrentTenant();
  const { billingStatus } = useBillingStatus({ currentTenant });
  const { setUserProperties } = useAnalytics();
  let lastAppliedPropertiesKey: string | undefined;

  watch(
    () =>
      buildAnalyticsUserProperties({
        billingStatus: billingStatus.value,
        currentTenant: currentTenant.value,
        isLoggedIn: authStore.csrfToken != null,
        whoAmI: whoAmIStore.whoAmI,
      }),
    (properties) => {
      const propertiesKey = getPropertiesCacheKey(properties);
      if (propertiesKey === lastAppliedPropertiesKey) return;

      setUserProperties(properties);
      lastAppliedPropertiesKey = propertiesKey;
    },
    { immediate: true },
  );
}
